/**
 * sd-logo-carousel.js
 *
 * Infinite seamless logo marquee for the Savor theme.
 * Recipe: clone the logo set once → track is 2× wide → plain CSS keyframe animates to -50%.
 * JS responsibility: clone, set --lc-duration, respect reduced-motion.
 *
 * Works in all browsers (Chrome, Safari, Firefox, Edge).
 * No library, no animation-timeline (Chrome-only), no embla.
 */

import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class SdLogoCarousel extends Component {
  requiredRefs = ['track'];

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  updatedCallback() {
    super.updatedCallback?.();
    // Re-clone after theme editor re-renders (block added/removed/reordered).
    this.#cleanupClones();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanupClones();
  }

  // ── Private ───────────────────────────────────────────────────────────────

  #init() {
    const track = this.refs.track;
    if (!track) return;

    // Sync the CSS variable from the data-attribute (schema setting).
    const speed = parseFloat(this.dataset.speed) || 30;
    this.style.setProperty('--lc-duration', `${speed}s`);

    // Under reduced-motion: skip cloning, no animation (CSS also removes it).
    if (prefersReducedMotion()) return;

    // Need at least 2 items to look right; clone if fewer than 6 to fill screen.
    const items = Array.from(track.children);
    if (items.length === 0) return;

    // Clone the entire set once so the seamless loop can translate to exactly -50%.
    const fragment = document.createDocumentFragment();
    items.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.lsClone = '1';
      fragment.appendChild(clone);
    });
    track.appendChild(fragment);
  }

  #cleanupClones() {
    if (!this.refs?.track) return;
    this.refs.track
      .querySelectorAll('[data-ls-clone]')
      .forEach((el) => el.remove());
  }
}

if (!customElements.get('sd-logo-carousel')) {
  customElements.define('sd-logo-carousel', SdLogoCarousel);
}
